/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { IoChevronDown } from 'react-icons/io5';

import { showError } from 'legacy/common/utils/toast';
import ImageUpload, {
  UploadAspectRatios
} from 'legacy/common/components/wysiwyg-form/ImageUpload';
import { useImageUploadCommand } from 'legacy/common/hooks/useImageUploadCommand';
import DateTimePickerField from 'legacy/components/util/FormHelpers/DateTimePickerField';
import Button from 'legacy/common/components/button/Button';
import { TierDto } from 'legacy/events/types';
import { GoToStepFn, StepsId } from '../types';

import formStyles from 'legacy/common/components/form/Form.module.scss';
import computeMemberOnlyLabel from '../helpers/computeMemberOnlyLabel';

type Props = {
  isEditing: boolean;
  ticket?: TierDto;
  goToStep: GoToStepFn;
  isUnsaved: (field: string) => boolean;
};

const AdditionalOptions = ({
  isEditing,
  ticket,
  goToStep,
  isUnsaved
}: Props) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const uploadImageCommand = useImageUploadCommand();
  const [showEndDate, setShowEndDate] = useState(Boolean(ticket?.endDate));
  const [isAdditionalOptionsVisible, setIsAdditionalOptionsVisible] = useState(
    Boolean(values?.imageUrl) ||
      Boolean(ticket?.startDate) ||
      Boolean(values?.requiredOrganizationIds?.length) ||
      Boolean(values?.requiredAssociationIds?.length)
  );

  const toggleAdditionalOptions = () =>
    setIsAdditionalOptionsVisible((prevValue) => !prevValue);

  const toggleEndTime = () => {
    delete values.endDate;
    delete (values as any).endDateTime;
    setShowEndDate(!showEndDate);
  };

  const onImageUploadSuccess = ({ imageUrl }) => {
    setFieldValue('imageUrl', imageUrl);
  };

  const onImageUploadError = (err) => {
    showError({
      title: 'Error uploading image',
      message: err.message
    });
  };

  return (
    <>
      <div
        className="mt-4 text-sm text-primary-500 cursor-pointer"
        onClick={toggleAdditionalOptions}
      >
        {isAdditionalOptionsVisible
          ? 'Hide additional options'
          : 'Show additional options'}
      </div>
      {isAdditionalOptionsVisible && (
        <div className={formStyles.group}>
          <div className="mb-8">
            <div className="text-sm font-normal pb-2">Member-Only Access</div>
            <button
              className="border-1 flex flex-1 w-full p-3.5 rounded-lg items-center"
              type="button"
              onClick={() => goToStep(StepsId.MemberOnlyAccess)}
            >
              {values.requiredOrganizationIds.length === 0 &&
              values.requiredAssociationIds.length === 0 ? (
                <div className="flex-1 text-left text-neutral-400">
                  Select Organizations and/or Associations
                </div>
              ) : (
                <div className="flex-1 text-left">
                  {computeMemberOnlyLabel(
                    values.requiredAssociationIds,
                    values.requiredOrganizationIds
                  )}
                </div>
              )}
              <IoChevronDown size={15} />
            </button>
          </div>
          <div className="mb-8">
            <ImageUpload
              label="Add your 3:2 cover photo*"
              maxLabel="Max 3840 x 2560"
              aspectRatio={UploadAspectRatios.ThreeToTwo}
              photoUrl={values?.imageUrl}
              isUploading={uploadImageCommand.isLoading}
              onUploadPhoto={(blob) => {
                uploadImageCommand.mutate(
                  { blob },
                  {
                    onSuccess: onImageUploadSuccess,
                    onError: onImageUploadError
                  }
                );
              }}
              error=""
            />
          </div>

          {/*
            Show time options in the following cases:
              - Tier is being created
              - Tier is being edited and has a start date
              - Tier is being edited and has an end date
           */}
          {(!isEditing ||
            Boolean(ticket.startDate) ||
            Boolean(ticket.endDate)) && (
            <div className="mt-4">
              {(!isEditing || Boolean(ticket.startDate)) && (
                <>
                  <label className={formStyles.label}>{`Start Date/Time ${
                    isUnsaved('startDate') ? '(unsaved)' : ''
                  }`}</label>
                  <DateTimePickerField name="startDate" />
                </>
              )}

              {showEndDate && (
                <div className="mt-4">
                  <label className={formStyles.label}>{`End Date/Time ${
                    isUnsaved('endDate') ? '(unsaved)' : ''
                  }`}</label>
                  <DateTimePickerField
                    name="endDate"
                    disabledBefore={new Date(values.startDate)}
                  />
                </div>
              )}

              <div className="mt-4 mb-20">
                <Button isSubmit={false} onClick={toggleEndTime}>
                  {showEndDate ? 'Remove End Time' : 'Show End Time'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AdditionalOptions;
